/**
 * Path functionality.
 * @module path
 * @license MIT
 *
 * @copyright 2011 Alexis Deveria, 2011 Jeff Schiller
 */

import { NS } from '../common/namespaces';
import { transformPoint, getMatrix, XYObject } from './math';
import { assignAttributes, getRotationAngle, isNullish, getElem, BBox } from './utilities';
import { supportsPathInsertItemBefore, supportsPathReplaceItem, isWebkit } from '../common/browser';
import { PathMethodsContext } from './path';
import { ChangeElementCommand } from './undocommands';
import SvgCanvas from './svgcanvas';

let pathMethodsContext_: PathMethodsContext = null;
let svgCanvas: SvgCanvas;
/**
* @function module:path-actions.init
* @param {module:path-actions.pathMethodsContext_} pathMethodsContext
* @returns {void}
*/
export const init = function (_svgCanvas: SvgCanvas, pathMethodsContext: PathMethodsContext) {
    svgCanvas = _svgCanvas;
    pathMethodsContext_ = pathMethodsContext;
};

/**
* @function module:path.insertItemBefore
* @param {SVGPathElement} elem
* @param {Segment} newseg
* @param {Integer} index
* @returns {void}
*/
export const insertItemBeforeMethod = function (elem: SVGPathElement, newseg: SVGPathSeg, index: Integer) {
    // Support insertItemBefore on paths for FF2
    const list = elem.pathSegList;

    if (supportsPathInsertItemBefore()) {
        list.insertItemBefore(newseg, index);
        return;
    }
    const len = list.numberOfItems;
    const arr = [];
    for (let i = 0; i < len; i++) {
        const curSeg = list.getItem(i);
        arr.push(curSeg);
    }
    list.clear();
    for (let i = 0; i < len; i++) {
        if (i === index) { // index + 1
            list.appendItem(newseg);
        }
        list.appendItem(arr[i]);
    }
};

type SegItem = SVGPathSegMovetoAbs | SVGPathSegLinetoAbs | SVGPathSegCurvetoCubicAbs | SVGPathSegCurvetoQuadraticAbs | SVGPathSegArcAbs | SVGPathSegLinetoHorizontalAbs | SVGPathSegLinetoVerticalAbs | SVGPathSegCurvetoCubicSmoothAbs | SVGPathSegCurvetoQuadraticSmoothAbs;

/**
* @function module:path.ptObjToArr
* @todo See if this should just live in `replacePathSeg`
* @param {number} type
* @param {SVGPathSegMovetoAbs|SVGPathSegLinetoAbs|SVGPathSegCurvetoCubicAbs|SVGPathSegCurvetoQuadraticAbs|SVGPathSegArcAbs|SVGPathSegLinetoHorizontalAbs|SVGPathSegLinetoVerticalAbs|SVGPathSegCurvetoCubicSmoothAbs|SVGPathSegCurvetoQuadraticSmoothAbs} segItem
* @returns {ArgumentsArray}
*/
export const ptObjToArrMethod = function (type: number, segItem: SegItem) {
    const segData = pathMethodsContext_.getSegData();
    const props = segData[type];

    return props.map((prop) => {
        return segItem[prop];
    });
};

/**
* @function module:path.getGripPt
* @param {Segment} seg
* @param {module:math.XYObject} altPt
* @returns {module:math.XYObject}
*/
export const getGripPtMethod = function (seg: Segment, altPt?: XYObject): XYObject {
    const { path: pth } = seg;
    let out = {
        x: altPt ? altPt.x : seg.item.x,
        y: altPt ? altPt.y : seg.item.y
    };

    if (pth.matrix) {
        const pt = transformPoint(out.x, out.y, pth.matrix);
        out = pt;
    }
    const currentZoom = svgCanvas.getCurrentZoom();
    out.x *= currentZoom;
    out.y *= currentZoom;

    return out;
};

/**
* @function module:path.getPointFromGrip
* @param {module:math.XYObject} pt
* @param {module:path.Path} pth
* @returns {module:math.XYObject}
*/
export const getPointFromGripMethod = function (pt: XYObject, pth: Path): XYObject {
    const out = {
        x: pt.x,
        y: pt.y
    };

    if (pth.matrix) {
        pt = transformPoint(out.x, out.y, pth.imatrix);
        out.x = pt.x;
        out.y = pt.y;
    }
    const currentZoom = svgCanvas.getCurrentZoom();
    out.x /= currentZoom;
    out.y /= currentZoom;

    return out;
};
/**
* @function module:path.getGripContainer
* @returns {Element}
*/
export const getGripContainerMethod = function (): Element {
    let c = getElem('pathpointgrip_container');

    if (!c) {
        const parentElement = getElem('selectorParentGroup');
        c = document.createElementNS(NS.SVG, 'g');
        parentElement.append(c);
        c.id = 'pathpointgrip_container';
    }

    return c;
};

/**
* Requires prior call to `setUiStrings` if `xlink:title`
*    to be set on the grip.
* @function module:path.addPointGrip
* @param {Integer} index
* @param {Integer} x
* @param {Integer} y
* @returns {SVGCircleElement}
*/
export const addPointGripMethod = function (index: Integer, x?: Integer, y?: Integer) {
    // create the container of all the point grips
    const pointGripContainer = getGripContainerMethod();

    let pointGrip = getElem('pathpointgrip_' + index);
    // create it
    if (!pointGrip) {
        pointGrip = document.createElementNS(NS.SVG, 'circle');
        const atts = {
            id: 'pathpointgrip_' + index,
            display: 'none',
            r: 4,
            fill: '#0FF',
            stroke: '#00F',
            'stroke-width': 2,
            cursor: 'move',
            style: 'pointer-events:all'
        };
        const uiStrings = svgCanvas.getUIStrings();
        if ('pathNodeTooltip' in uiStrings) { // May be empty if running path.ts without svg-editor
            atts['xlink:title'] = uiStrings.pathNodeTooltip;
        }
        assignAttributes(pointGrip, atts);
        pointGripContainer.append(pointGrip);

        const grip = document.getElementById('pathpointgrip_' + index);
        grip?.addEventListener("dblclick", () => {
            const path = pathMethodsContext_.getPathObj();
            if (path) {
                path.setSegType();
            }
        });
    }
    if (x && y) {
        // set up the point grip element and display it
        assignAttributes(pointGrip, {
            cx: x,
            cy: y,
            display: 'inline'
        });
    }
    return pointGrip;
};

/**
* Requires prior call to `setUiStrings` if `xlink:title`
*    to be set on the grip.
* @function module:path.addCtrlGrip
* @param {string} id
* @returns {SVGCircleElement}
*/
export const addCtrlGripMethod = function (id: string): SVGCircleElement {
    let pointGrip = getElem<SVGCircleElement>('ctrlpointgrip_' + id);
    if (pointGrip) {
        return pointGrip;
    }

    pointGrip = document.createElementNS(NS.SVG, 'circle');
    const atts = {
        id: 'ctrlpointgrip_' + id,
        display: 'none',
        r: 4,
        fill: '#0FF',
        stroke: '#55F',
        'stroke-width': 1,
        cursor: 'move',
        style: 'pointer-events:all'
    };

    const uiStrings = svgCanvas.getUIStrings();
    if ('pathCtrlPtTooltip' in uiStrings) { // May be empty if running path.ts without svg-editor
        atts['xlink:title'] = uiStrings.pathCtrlPtTooltip;
    }

    assignAttributes(pointGrip, atts);
    getGripContainerMethod().append(pointGrip);

    return pointGrip;
};
/**
* @function module:path.getCtrlLine
* @param {string} id
* @returns {SVGLineElement}
*/
export const getCtrlLineMethod = function (id: string): SVGLineElement {
    let ctrlLine = getElem<SVGLineElement>('ctrlLine_' + id);
    if (ctrlLine) {
        return ctrlLine;
    }

    ctrlLine = document.createElementNS(NS.SVG, 'line');
    assignAttributes(ctrlLine, {
        id: 'ctrlLine_' + id,
        stroke: '#555',
        'stroke-width': 1,
        style: 'pointer-events:none'
    });

    getGripContainerMethod().append(ctrlLine);
    return ctrlLine;
};

/**
* @function module:path.getPointGrip
* @param {Segment} seg
* @param {boolean} update
* @returns {SVGCircleElement}
*/
export const getPointGripMethod = function (seg: Segment, update: boolean) {
    const { index } = seg;
    const pointGrip = addPointGripMethod(index);

    if (update) {
        const pt = getGripPtMethod(seg);
        assignAttributes(pointGrip, {
            cx: pt.x,
            cy: pt.y,
            display: 'inline'
        });
    }

    return pointGrip;
};
/**
* @function module:path.getControlPoints
* @param {Segment} seg
* @returns {PlainObject<string, SVGLineElement|SVGCircleElement>}
*/
export const getControlPointsMethod = function (seg: Segment): { [key: string]: SVGLineElement | SVGCircleElement } {
    const { item, index } = seg;

    if (!('x1' in item) || !('x2' in item)) {
        return null;
    }

    const cpt = {};
  /* const pointGripContainer = */ getGripContainerMethod();

    // Note that this is intentionally not seg.prev.item
    const path = pathMethodsContext_.getPathObj();
    const prev = path.segs[index - 1].item;

    const segItems = [prev, item];

    for (let i = 1; i < 3; i++) {
        const id = index + 'c' + i;

        const ctrlLine = cpt['c' + i + '_line'] = getCtrlLineMethod(id);

        const pt = getGripPtMethod(seg, { x: item['x' + i], y: item['y' + i] });
        const gpt = getGripPtMethod(seg, { x: segItems[i - 1].x, y: segItems[i - 1].y });

        assignAttributes(ctrlLine, {
            x1: pt.x,
            y1: pt.y,
            x2: gpt.x,
            y2: gpt.y,
            display: 'inline'
        });

        cpt['c' + i + '_line'] = ctrlLine;

        // create it
        const pointGrip = cpt['c' + i] = addCtrlGripMethod(id);

        assignAttributes(pointGrip, {
            cx: pt.x,
            cy: pt.y,
            display: 'inline'
        });
        cpt['c' + i] = pointGrip;
    }
    return cpt;
};
/**
* This replaces the segment at the given index. Type is given as number.
* @function module:path.replacePathSeg
* @param {Integer} type Possible values set during {@link module:path.init}
* @param {Integer} index
* @param {ArgumentsArray} pts
* @param {SVGPathElement} elem
* @returns {void}
*/
export const replacePathSegMethod = function (type: number, index: number, pts: ArgumentsArray, elem?: SVGPathElement) {
    const path = pathMethodsContext_.getPathObj();
    const pth = elem || path.elem;
    const pathFuncs = pathMethodsContext_.getPathFuncs();
    const func = 'createSVGPathSeg' + pathFuncs[type];
    const seg = pth[func](...pts);

    if (supportsPathReplaceItem()) {
        pth.pathSegList.replaceItem(seg, index);
    } else {
        const segList = pth.pathSegList;
        const len = segList.numberOfItems;
        const arr = [];
        for (let i = 0; i < len; i++) {
            const curSeg = segList.getItem(i);
            arr.push(curSeg);
        }
        segList.clear();
        for (let i = 0; i < len; i++) {
            if (i === index) {
                segList.appendItem(seg);
            } else {
                segList.appendItem(arr[i]);
            }
        }
    }
};
/**
* @function module:path.getSegSelector
* @param {Segment} seg
* @param {boolean} update
* @returns {SVGPathElement}
*/
export const getSegSelectorMethod = function (seg: Segment, update: boolean): SVGPathElement {
    const { index } = seg;

    let segLine = getElem<SVGPathElement>('segline_' + index);

    if (!segLine) {
        const pointGripContainer = getGripContainerMethod();
        // create segline
        segLine = document.createElementNS(NS.SVG, 'path');
        assignAttributes(segLine, {
            id: 'segline_' + index,
            display: 'none',
            fill: 'none',
            stroke: '#0FF',
            'stroke-width': 2,
            style: 'pointer-events:none',
            d: 'M0,0 0,0'
        });
        pointGripContainer.append(segLine);
    }

    if (update) {
        const { prev } = seg;
        if (!prev) {
            segLine.setAttribute('display', 'none');
            return segLine;
        }

        const pt = getGripPtMethod(prev);
        // Set start point
        replacePathSegMethod(2, 0, [pt.x, pt.y], segLine);

        const pts = ptObjToArrMethod(seg.type, seg.item); // , true);
        for (let i = 0; i < pts.length; i += 2) {
            const point = getGripPtMethod(seg, { x: pts[i], y: pts[i + 1] });
            pts[i] = point.x;
            pts[i + 1] = point.y;
        }

        replacePathSegMethod(seg.type, 1, pts, segLine);
    }

    return segLine;
};
/**
*
*/
export class Segment {
    public selected: boolean = false;
    public index: number;
    public item: any;
    public type: number;

    public path: Path;

    public ctrlpts: any = [];
    public ptgrip: any = null;
    public segsel: any = null;

    public prev: any;
    public next: any;
    public mate: any;

    /**
    * @param {Integer} index
    * @param {SVGPathSeg} item
    * @todo Is `item` be more constrained here?
    */
    constructor(index: number, item: any) {
        this.index = index;
        this.item = item;
        this.type = item.pathSegType;
    }

    /**
     * @param {boolean} y
     * @returns {void}
     */
    showCtrlPts(y: boolean) {
        for (const i in this.ctrlpts) {
            if ({}.hasOwnProperty.call(this.ctrlpts, i)) {
                this.ctrlpts[i].setAttribute('display', y ? 'inline' : 'none');
            }
        }
    }

    /**
     * @param {boolean} y
     * @returns {void}
     */
    selectCtrls(y: boolean) {
        document.getElementById('ctrlpointgrip_' + this.index + 'c1').setAttribute('fill', y ? '#0FF' : '#EEE');
        document.getElementById('ctrlpointgrip_' + this.index + 'c2').setAttribute('fill', y ? '#0FF' : '#EEE')
    }

    /**
     * @param {boolean} y
     * @returns {void}
     */
    show(y: boolean) {
        if (this.ptgrip) {
            this.ptgrip.setAttribute('display', y ? 'inline' : 'none');
            this.segsel.setAttribute('display', y ? 'inline' : 'none');
            // Show/hide all control points if available
            this.showCtrlPts(y);
        }
    }

    /**
     * @param {boolean} y
     * @returns {void}
     */
    select(y: boolean) {
        if (this.ptgrip) {
            this.ptgrip.setAttribute('stroke', y ? '#0FF' : '#00F');
            this.segsel.setAttribute('display', y ? 'inline' : 'none');
            if (this.ctrlpts) {
                this.selectCtrls(y);
            }
            this.selected = y;
        }
    }

    /**
     * @returns {void}
     */
    addGrip() {
        this.ptgrip = getPointGripMethod(this, true);
        this.ctrlpts = getControlPointsMethod(this); // , true);
        this.segsel = getSegSelectorMethod(this, true);
    }

    /**
     * @param {boolean} full
     * @returns {void}
     */
    update(full?: boolean) {
        if (this.ptgrip) {
            const pt = getGripPtMethod(this);
            assignAttributes(this.ptgrip, {
                cx: pt.x,
                cy: pt.y
            });

            getSegSelectorMethod(this, true);

            if (this.ctrlpts) {
                if (full) {
                    const path = pathMethodsContext_.getPathObj();
                    this.item = path.elem.pathSegList.getItem(this.index);
                    this.type = this.item.pathSegType;
                }
                getControlPointsMethod(this);
            }
            // this.segsel.setAttribute('display', y ? 'inline' : 'none');
        }
    }

    /**
     * @param {Integer} dx
     * @param {Integer} dy
     * @returns {void}
     */
    move(dx: Integer, dy: Integer) {
        const { item } = this;

        let curPts: number[];
        if (this.ctrlpts) {
            item.x += dx;
            item.y += dy;
            item.x2 += dx;
            item.y2 += dy;

            curPts = [
                item.x, item.y,
                item.x1, item.y1,
                item.x2, item.y2
            ];
        } else {
            item.x += dx;
            item.y += dy;
            curPts = [item.x, item.y];
        }

        replacePathSegMethod(
            this.type,
            this.index,
            // type 10/11 means ARC
            (this.type === 10 || this.type === 11) ? ptObjToArrMethod(this.type, item) : curPts
        );

        if (this.next?.ctrlpts) {
            const next = this.next.item;
            next.x1 += dx;
            next.y1 += dy;
            const nextPts = [
                next.x, next.y,
                next.x1, next.y1,
                next.x2, next.y2
            ];
            replacePathSegMethod(this.next.type, this.next.index, nextPts);
        }

        if (this.mate) {
            // The last point of a closed subpath has a 'mate',
            // which is the 'M' segment of the subpath
            const { item: itm } = this.mate;
            itm.x += dx;
            itm.y += dy;
            const pts = [itm.x, itm.y];
            replacePathSegMethod(this.mate.type, this.mate.index, pts);
            // Has no grip, so does not need 'updating'?
        }

        this.update(true);
        if (this.next) {
            this.next.update(true);
        }
    }

    /**
     * @param {Integer} num
     * @returns {void}
     */
    setLinked(num: Integer) {
        let seg, anum, pt;

        if (num === 2) {
            anum = 1;
            seg = this.next;
            if (!seg) { return; }
            pt = this.item;
        } else {
            anum = 2;
            seg = this.prev;
            if (!seg) { return; }
            pt = seg.item;
        }

        const { item } = seg;
        item['x' + anum] = pt.x + (pt.x - this.item['x' + num]);
        item['y' + anum] = pt.y + (pt.y - this.item['y' + num]);

        const pts = [
            item.x, item.y,
            item.x1, item.y1,
            item.x2, item.y2
        ];

        replacePathSegMethod(seg.type, seg.index, pts);
        seg.update(true);
    }

    /**
     * @param {Integer} num
     * @param {Integer} dx
     * @param {Integer} dy
     * @returns {void}
     */
    moveCtrl(num: Integer, dx: Integer, dy: Integer) {
        const { item } = this;
        item['x' + num] += dx;
        item['y' + num] += dy;

        const pts = [
            item.x, item.y,
            item.x1, item.y1, item.x2, item.y2
        ];

        replacePathSegMethod(this.type, this.index, pts);
        this.update(true);
    }

    /**
     * @param {Integer} newType Possible values set during {@link module:path.init}
     * @param {ArgumentsArray} pts
     * @returns {void}
     */
    setType(newType: Integer, pts: ArgumentsArray) {
        replacePathSegMethod(newType, this.index, pts);
        this.type = newType;
        const path = pathMethodsContext_.getPathObj();
        this.item = path.elem.pathSegList.getItem(this.index);
        this.showCtrlPts(newType === 6);
        this.ctrlpts = getControlPointsMethod(this);
        this.update(true);
    }
}


/**
* @callback module:path.PathEachSegCallback
* @this module:path.Segment
* @param {Integer} i The index of the seg being iterated
* @returns {boolean|void} Will stop execution of `eachSeg` if returns `false`
*/
type PathEachSegCallback = (this: Segment, i: Integer) => boolean | void;

/**
*
*/
export class Path {
    public elem: SVGPathElement;
    public segs: any[] = [];
    public selected_pts: any[] = [];

    public first_seg: any;
    public last_d: any;

    public matrix: DOMMatrix;
    public imatrix: DOMMatrix;

    public dragctrl: any;

    public oldbbox: BBox;
    public cur_pt: Integer;
    public dragging: [Integer, Integer] | boolean;

    /**
    * @param {SVGPathElement} elem
    * @throws {Error} If constructed without a path element
    */
    constructor(elem: SVGPathElement) {
        if (!elem || elem.tagName !== 'path') {
            throw new Error('svgedit.path.Path constructed without a <path> element');
        }

        this.elem = elem;
        pathMethodsContext_.setPathObj(this);
        // path = this;

        this.init();
    }

    /**
    * Reset path data.
    * @returns {module:path.Path}
    */
    init() {
        // Hide all grips, etc

        // fixed, needed to work on all found elements, not just first
        const pointGripContainer = getGripContainerMethod();
        const elements = pointGripContainer.querySelectorAll<SVGGraphicsElement>('*');
        for(const el of elements) {
            el.setAttribute("display", "none");
        }

        const segList = this.elem.pathSegList;
        const len = segList.numberOfItems;
        this.segs = [];
        this.selected_pts = [];
        this.first_seg = null;

        // Set up segs array
        for (let i = 0; i < len; i++) {
            const item = segList.getItem(i);
            const segment = new Segment(i, item);
            segment.path = this;
            this.segs.push(segment);
        }

        const { segs } = this;

        let startI = null;
        for (let i = 0; i < len; i++) {
            const seg = segs[i];
            const nextSeg = (i + 1) >= len ? null : segs[i + 1];
            const prevSeg = (i - 1) < 0 ? null : segs[i - 1];
            if (seg.type === 2) {
                if (prevSeg && prevSeg.type !== 1) {
                    // New sub-path, last one is open,
                    // so add a grip to last sub-path's first point
                    const startSeg = segs[startI];
                    startSeg.next = segs[startI + 1];
                    startSeg.next.prev = startSeg;
                    startSeg.addGrip();
                }
                // Remember that this is a starter seg
                startI = i;
            } else if (nextSeg && nextSeg.type === 1) {
                // This is the last real segment of a closed sub-path
                // Next is first seg after "M"
                seg.next = segs[startI + 1];

                // First seg after "M"'s prev is this
                seg.next.prev = seg;
                seg.mate = segs[startI];
                seg.addGrip();
                if (isNullish(this.first_seg)) {
                    this.first_seg = seg;
                }
            } else if (!nextSeg) {
                if (seg.type !== 1) {
                    // Last seg, doesn't close so add a grip
                    // to last sub-path's first point
                    const startSeg = segs[startI];
                    startSeg.next = segs[startI + 1];
                    startSeg.next.prev = startSeg;
                    startSeg.addGrip();
                    seg.addGrip();

                    if (!this.first_seg) {
                        // Open path, so set first as real first and add grip
                        this.first_seg = segs[startI];
                    }
                }
            } else if (seg.type !== 1) {
                // Regular segment, so add grip and its "next"
                seg.addGrip();

                // Don't set its "next" if it's an "M"
                if (nextSeg && nextSeg.type !== 2) {
                    seg.next = nextSeg;
                    seg.next.prev = seg;
                }
            }
        }
        return this;
    }

    /**
    * @param {module:path.PathEachSegCallback} fn
    * @returns {void}
    */
    eachSeg(fn: PathEachSegCallback) {
        const len = this.segs.length;

        for (let i = 0; i < len; i++) {
            const ret = fn.call(this.segs[i], i);

            if (ret === false) {
                break;
            }
        }
    }

    /**
    * @param {Integer} index
    * @returns {void}
    */
    addSeg(index: Integer) {
        // Adds a new segment
        const seg = this.segs[index];

        if (!seg.prev) {
            return;
        }

        const { prev } = seg;
        let newseg, newX, newY;
        switch (seg.item.pathSegType) {
            case 4: {
                newX = (seg.item.x + prev.item.x) / 2;
                newY = (seg.item.y + prev.item.y) / 2;
                newseg = this.elem.createSVGPathSegLinetoAbs(newX, newY);
                break;
            } case 6: { // make it a curved segment to preserve the shape (WRS)
                // https://en.wikipedia.org/wiki/De_Casteljau%27s_algorithm#Geometric_interpretation
                const p0x = (prev.item.x + seg.item.x1) / 2;
                const p1x = (seg.item.x1 + seg.item.x2) / 2;
                const p2x = (seg.item.x2 + seg.item.x) / 2;
                const p01x = (p0x + p1x) / 2;
                const p12x = (p1x + p2x) / 2;
                newX = (p01x + p12x) / 2;
                const p0y = (prev.item.y + seg.item.y1) / 2;
                const p1y = (seg.item.y1 + seg.item.y2) / 2;
                const p2y = (seg.item.y2 + seg.item.y) / 2;
                const p01y = (p0y + p1y) / 2;
                const p12y = (p1y + p2y) / 2;
                newY = (p01y + p12y) / 2;
                newseg = this.elem.createSVGPathSegCurvetoCubicAbs(newX, newY, p0x, p0y, p01x, p01y);
                const pts = [seg.item.x, seg.item.y, p12x, p12y, p2x, p2y];
                replacePathSegMethod(seg.type, index, pts);
                break;
            }
        }

        insertItemBeforeMethod(this.elem, newseg, index);
    }

    /**
    * @param {Integer} index
    * @returns {void}
    */
    deleteSeg(index: Integer) {
        const seg = this.segs[index];
        const list = this.elem.pathSegList;

        seg.show(false);
        const { next } = seg;

        if (seg.mate) {
            // Make the next point be the "M" point
            const pt = [next.item.x, next.item.y];
            replacePathSegMethod(2, next.index, pt);

            // Reposition last node
            replacePathSegMethod(4, seg.index, pt);

            list.removeItem(seg.mate.index);
        } else if (!seg.prev) {
            // First node of open path, make next point the M
            // const {item} = seg;
            const pt = [next.item.x, next.item.y];
            replacePathSegMethod(2, seg.next.index, pt);
            list.removeItem(index);
        } else {
            list.removeItem(index);
        }
    }

    /**
    * @param {Integer} index
    * @returns {void}
    */
    removePtFromSelection(index: Integer) {
        const pos = this.selected_pts.indexOf(index);
        if (pos === -1) {
            return;
        }
        this.segs[index].select(false);
        this.selected_pts.splice(pos, 1);
    }

    /**
    * @returns {void}
    */
    clearSelection() {
        this.eachSeg(function () {
            // 'this' is the segment here
            this.select(false);
        });
        this.selected_pts = [];
    }

    /**
    * @returns {void}
    */
    storeD() {
        this.last_d = this.elem.getAttribute('d');
    }

    /**
    * @param {boolean} y
    * @returns {Path}
    */
    show(y: boolean): Path {
        // Shows this path's segment grips
        this.eachSeg(function () {
            // 'this' is the segment here
            this.show(y);
        });

        if (y) {
            this.selectPt(this.first_seg.index);
        }

        return this;
    }

    /**
    * Move selected points.
    * @param {Integer} dx
    * @param {Integer} dy
    * @returns {void}
    */
    movePts(dx: Integer, dy: Integer) {
        let i = this.selected_pts.length;

        while (i--) {
            const seg = this.segs[this.selected_pts[i]];
            seg.move(dx, dy);
        }
    }

    /**
    * @param {Integer} dx
    * @param {Integer} dy
    * @returns {void}
    */
    moveCtrl(dx: Integer, dy: Integer) {
        const seg = this.segs[this.selected_pts[0]];

        seg.moveCtrl(this.dragctrl, dx, dy);

        if (pathMethodsContext_.getLinkControlPts()) {
            seg.setLinked(this.dragctrl);
        }
    }

    /**
    * @param {?Integer} newType See {@link https://www.w3.org/TR/SVG/single-page.html#paths-InterfaceSVGPathSeg}
    * @returns {void}
    */
    setSegType(newType?: Integer) {
        this.storeD();
        let i = this.selected_pts.length;
        let text;

        while (i--) {
            const selPt = this.selected_pts[i];

            // Selected seg
            const cur = this.segs[selPt];
            const { prev } = cur;
            if (!prev) { continue; }

            if (!newType) { // double-click, so just toggle
                text = 'Toggle Path Segment Type';

                // Toggle segment to curve/straight line
                const oldType = cur.type;

                newType = (oldType === 6) ? 4 : 6;
            }

            newType = Number(newType);

            const curX = cur.item.x;
            const curY = cur.item.y;
            const prevX = prev.item.x;
            const prevY = prev.item.y;
            let points;
            switch (newType) {
                case 6: {
                    if (cur.olditem) {
                        const old = cur.olditem;
                        points = [curX, curY, old.x1, old.y1, old.x2, old.y2];
                    } else {
                        const diffX = curX - prevX;
                        const diffY = curY - prevY;
                        // get control points from straight line segment
                        /*
                        const ct1x = (prevX + (diffY/2));
                        const ct1y = (prevY - (diffX/2));
                        const ct2x = (curX + (diffY/2));
                        const ct2y = (curY - (diffX/2));
                        */
                        // create control points on the line to preserve the shape (WRS)
                        const ct1x = (prevX + (diffX / 3));
                        const ct1y = (prevY + (diffY / 3));
                        const ct2x = (curX - (diffX / 3));
                        const ct2y = (curY - (diffY / 3));
                        points = [curX, curY, ct1x, ct1y, ct2x, ct2y];
                    }
                    break;
                } case 4: {
                    points = [curX, curY];

                    // Store original prevve segment nums
                    cur.olditem = cur.item;
                    break;
                }
            }

            cur.setType(newType, points);
        }
        const path = pathMethodsContext_.getPathObj();
        path.endChanges(text);
    }

    /**
    * @param {Integer} pt
    * @param {Integer} [ctrlNum]
    * @returns {void}
    */
    selectPt(pt?: Integer, ctrlNum?: Integer) {
        this.clearSelection();

        if (isNullish(pt)) {
            this.eachSeg(function (i) {
                // 'this' is the segment here.
                if (this.prev) {
                    pt = i;
                }
            });
        }

        this.addPtsToSelection(pt);
        if (ctrlNum) {
            this.dragctrl = ctrlNum;

            if (pathMethodsContext_.getLinkControlPts()) {
                this.segs[pt].setLinked(ctrlNum);
            }
        }
    }

    /**
    * Update position of all points.
    * @returns {Path}
    */
    update(): Path {
        const { elem } = this;
        if (getRotationAngle(elem)) {
            this.matrix = getMatrix(elem);
            this.imatrix = this.matrix.inverse();
        } else {
            this.matrix = null;
            this.imatrix = null;
        }

        this.eachSeg(function (i) {
            this.item = elem.pathSegList.getItem(i);
            this.update();
        });

        return this;
    }

    /**
    * @param {string} text
    * @returns {void}
    */
    endChanges(text: string) {
        const editorContext = pathMethodsContext_.getEditorContext();

        if (isWebkit()) {
            editorContext.resetD(this.elem);
        }

        const cmd = new ChangeElementCommand(this.elem, { d: this.last_d }, text);
        editorContext.endChanges({ cmd, elem: this.elem });
    }

    /**
    * @param {Integer|Integer[]} indexes
    * @returns {void}
    */
    addPtsToSelection(indexes: Integer | Integer[]) {
        const editorContext = pathMethodsContext_.getEditorContext();

        if (!Array.isArray(indexes)) {
            indexes = [indexes];
        }

        indexes.forEach((index) => {
            const seg = this.segs[index];
            if (seg.ptgrip && !this.selected_pts.includes(index) && index >= 0) {
                this.selected_pts.push(index);
            }
        });

        this.selected_pts.sort((a,b) => a - b);
        let i = this.selected_pts.length;
        const grips = [];
        grips.length = i;

        // Loop through points to be selected and highlight each
        while (i--) {
            const pt = this.selected_pts[i];
            const seg = this.segs[pt];
            seg.select(true);
            grips[i] = seg.ptgrip;
        }

        const closedSubpath = Path.subpathIsClosed(this.selected_pts[0]);
        editorContext.addPtsToSelection({ grips, closedSubpath });
    }

    // STATIC
    /**
    * @param {Integer} index
    * @returns {boolean}
    */
    public static subpathIsClosed(index: Integer): boolean {
        let clsd = false;
        // Check if subpath is already open
        const path = pathMethodsContext_.getPathObj();
        path.eachSeg(function (i) {
            if (i <= index) { return true; }
            if (this.type === 2) {
                // Found M first, so open
                return false;
            }
            if (this.type === 1) {
                // Found Z first, so closed
                clsd = true;
                return false;
            }
            return true;
        });

        return clsd;
    }
}
